import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { submitOnlineOrQueue } from "@/utils/offlineQueue";
import { CheckCircle2, AlertCircle, Clock, CloudOff } from "lucide-react";

const ProductionStage2 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [stage2, setStage2] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [form, setForm] = useState({
    strokes: "", ng_qty: "", loss_e_mins: "0", loss_m_mins: "0",
    loss_t_mins: "0", loss_o_mins: "0", loss_remarks: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/production/open-jobs")
      .then((r) => {
        setJobs(r.data);
        if (jobId) {
          const job = r.data.find((j) => j.job_id === jobId);
          if (job) setSelectedJob(job);
          else {
            // Job might be complete, fetch directly
            api.get(`/production/job/${jobId}`).then((jr) => {
              if (jr.data.job) setSelectedJob(jr.data.job);
            }).catch(() => {});
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingJobs(false));
  }, [jobId]);

  // Auto-fill strokes from the live counter (the IoT device bolted to this
  // machine reports strokes to /api/iot/counter, which keeps job.live_strokes
  // updated). The operator can still adjust it and must enter NG manually.
  useEffect(() => {
    const live = Number(selectedJob?.live_strokes || 0);
    if (selectedJob && live > 0) {
      setForm((f) => ({ ...f, strokes: String(live) }));
    }
  }, [selectedJob]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const strokes = parseInt(form.strokes) || 0;
  const cavities = selectedJob?.cavities || 1;
  const totalParts = strokes * cavities;
  const ngQty = parseInt(form.ng_qty) || 0;
  const okQty = totalParts - ngQty;
  const target = selectedJob?.target_per_day || 1;
  const efficiency = target > 0 ? Math.round((okQty / target) * 100) : 0;

  const getTimeColor = (hours) => {
    if (hours < 4) return "text-[#15803D] bg-green-50 border-green-200";
    if (hours < 8) return "text-[#D97706] bg-amber-50 border-amber-200";
    return "text-[#DC2626] bg-red-50 border-red-200";
  };

  const handleSubmit = async () => {
    if (!selectedJob) return;
    if (!form.strokes || parseInt(form.strokes) <= 0) {
      setError("Strokes must be greater than 0");
      return;
    }
    if (ngQty > totalParts) {
      setError("NG quantity cannot exceed total parts");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await submitOnlineOrQueue(api, {
        method: "post",
        url: `/production/stage2/${selectedJob.job_id}`,
        payload: {
          strokes: parseInt(form.strokes),
          ng_qty: ngQty,
          loss_e_mins: parseInt(form.loss_e_mins) || 0,
          loss_m_mins: parseInt(form.loss_m_mins) || 0,
          loss_t_mins: parseInt(form.loss_t_mins) || 0,
          loss_o_mins: parseInt(form.loss_o_mins) || 0,
          loss_remarks: form.loss_remarks,
        },
        label: `Stage-2 strokes for job ${selectedJob.job_id}`,
      });
      if (result.online) {
        setSuccess(result.data);
      } else {
        // Optimistic offline acknowledgement
        setSuccess({
          total_parts: totalParts,
          ok_qty: okQty,
          job_id: selectedJob.job_id,
          _queued: true,
        });
        window.__amplOfflineRefresh?.();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="Production Update" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className={`w-20 h-20 ${success._queued ? "bg-amber-100" : "bg-green-100"} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {success._queued
              ? <CloudOff size={48} className="text-amber-600" />
              : <CheckCircle2 size={48} className="text-[#15803D]" />}
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">
            {success._queued ? "Queued — Will Sync" : "Job Completed!"}
          </h2>
          <p className="text-slate-500 mb-4">
            {success._queued
              ? "Offline — submission saved on this device. It will sync automatically when WiFi returns."
              : "कार्य पूर्ण हुआ"}
          </p>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-left space-y-2 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Total Parts</span>
              <span className="font-bold">{success.total_parts}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">OK Parts</span>
              <span className="font-bold text-[#15803D]">{success.ok_qty}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">NG Parts</span>
              <span className="font-bold text-[#DC2626]">{ngQty}</span>
            </div>
          </div>
          {success.wip && !success._queued && (
            <div className={`rounded-2xl p-4 mb-6 text-left ${success.wip.fg_added ? "bg-emerald-50 border-2 border-emerald-200" : "bg-amber-50 border-2 border-amber-200"}`}>
              <p className={`text-xs font-extrabold uppercase tracking-widest mb-2 ${success.wip.fg_added ? "text-emerald-800" : "text-amber-800"}`}>
                Operation {success.wip.op_seq} of {success.wip.total_operations}
              </p>
              {success.wip.rm?.consumed_kg > 0 && (
                <Row label="📦 RM consumed" value={`${success.wip.rm.consumed_kg} kg`} accent="amber" />
              )}
              {success.wip.rm?.remaining_kg !== undefined && (
                <Row label={`📊 Batch ${success.wip.rm.batch_code} remaining`} value={`${success.wip.rm.remaining_kg} kg`} accent="amber" />
              )}
              {success.wip.wip_consumed > 0 && (
                <Row label="⬇ Pulled from WIP" value={`${success.wip.wip_consumed} pcs`} accent="emerald" />
              )}
              {success.wip.wip_added > 0 && (
                <Row label={`⬆ Added to WIP at op ${success.wip.op_seq}`} value={`${success.wip.wip_added} pcs`} accent="amber" bold />
              )}
              {success.wip.fg_added > 0 && (
                <Row label="🏆 Added to FG (Finished Goods)" value={`${success.wip.fg_added} pcs`} accent="emerald" bold />
              )}
              {(success.wip.warnings || []).map((w, i) => (
                <p key={i} className="text-xs text-red-700 mt-2 bg-red-50 rounded px-2 py-1">⚠ {w}</p>
              ))}
            </div>
          )}
          <button data-testid="stage2-done-btn" onClick={() => navigate("/home")}
            className="w-full h-12 bg-[#1A3560] text-white font-bold rounded-xl active:scale-[0.98] transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!selectedJob) {
    return (
      <div className="min-h-screen bg-slate-50 pb-4">
        <Header title="Update Production" showBack backPath="/home" />
        <div className="max-w-md mx-auto px-4 py-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
            Open Jobs / खुले कार्य ({jobs.length})
          </p>
          {loadingJobs ? (
            <div className="text-center py-12 text-slate-400">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400">
              <Clock size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No open jobs found</p>
              <p className="text-sm text-slate-300 mt-1">Start a production job first</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <button
                  key={job.job_id}
                  data-testid={`job-card-${job.job_id}`}
                  onClick={() => setSelectedJob(job)}
                  className="w-full bg-white rounded-2xl border border-slate-200 p-4 text-left hover:shadow-md active:scale-[0.98] transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-extrabold text-[#1A3560] text-base">{job.job_id}</p>
                      <p className="text-sm text-slate-600 mt-0.5">T{job.tool_no} — {job.part_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{job.machine_code} | {job.operator_name}</p>
                    </div>
                    <div className={`border rounded-lg px-3 py-1.5 ${getTimeColor(job.hours_elapsed || 0)}`}>
                      <p className="text-xs font-bold">{job.hours_elapsed?.toFixed(1)}h</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <Header title="Update Qty" showBack backPath="/production/update" />

      <div className="max-w-md mx-auto px-4 py-4">
        {/* Job summary */}
        <div className="bg-[#1A3560] rounded-2xl p-4 text-white mb-4">
          <p className="text-white/60 text-xs mb-1">Job ID</p>
          <p className="text-xl font-extrabold">{selectedJob.job_id}</p>
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <div><p className="text-white/50 text-xs">Tool</p><p className="font-bold">T{selectedJob.tool_no}</p></div>
            <div><p className="text-white/50 text-xs">Machine</p><p className="font-bold">{selectedJob.machine_code}</p></div>
            <div><p className="text-white/50 text-xs">Part</p><p className="font-bold text-xs">{selectedJob.part_name}</p></div>
            <div><p className="text-white/50 text-xs">Cavities</p><p className="font-bold">{selectedJob.cavities}</p></div>
          </div>
        </div>

        {/* Production qty */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4 mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Production Quantity / उत्पादन मात्रा</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Strokes / स्ट्रोक" required>
              <input data-testid="strokes-input" type="number" value={form.strokes} onChange={(e) => set("strokes", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
            <Field label="Cavities">
              <div className={`${INPUT_CLS} bg-slate-50 flex items-center px-3 text-slate-700 font-bold`}>{cavities}</div>
            </Field>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex justify-between items-center">
            <span className="text-sm text-blue-700 font-medium">Total Parts (Strokes × Cavities)</span>
            <span className="text-xl font-extrabold text-[#1A3560]">{totalParts}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="NG Qty / अस्वीकार">
              <input data-testid="ng-input" type="number" value={form.ng_qty} onChange={(e) => set("ng_qty", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
            <Field label="OK Qty (auto)">
              <div className={`${INPUT_CLS} bg-green-50 flex items-center px-3 font-bold text-[#15803D]`}>{okQty >= 0 ? okQty : 0}</div>
            </Field>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 flex justify-between items-center">
            <span className="text-sm text-slate-600">Efficiency vs Target ({target})</span>
            <span className={`text-lg font-extrabold ${efficiency >= 85 ? "text-[#15803D]" : efficiency >= 70 ? "text-[#D97706]" : "text-[#DC2626]"}`}>
              {efficiency}%
            </span>
          </div>
        </div>

        {/* Loss entry */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4 mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Loss Time (minutes) / नुकसान समय</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Power (E)">
              <input data-testid="loss-e" type="number" value={form.loss_e_mins} onChange={(e) => set("loss_e_mins", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
            <Field label="Machine (M)">
              <input data-testid="loss-m" type="number" value={form.loss_m_mins} onChange={(e) => set("loss_m_mins", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
            <Field label="Tool (T)">
              <input data-testid="loss-t" type="number" value={form.loss_t_mins} onChange={(e) => set("loss_t_mins", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
            <Field label="Other (O)">
              <input data-testid="loss-o" type="number" value={form.loss_o_mins} onChange={(e) => set("loss_o_mins", e.target.value)}
                placeholder="0" className={INPUT_CLS} min="0" />
            </Field>
          </div>
          <Field label="Loss Remarks">
            <textarea value={form.loss_remarks} onChange={(e) => set("loss_remarks", e.target.value)}
              placeholder="Describe loss reason..." rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1A3560] resize-none" />
          </Field>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          <button
            data-testid="stage2-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 bg-[#15803D] disabled:bg-slate-300 text-white font-bold text-base rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            {submitting ? <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Submitting...</> : "Submit & Close Job / जमा करें"}
          </button>
        </div>
      </div>
    </div>
  );
};

const INPUT_CLS = "w-full h-12 px-3 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3560]";
const Row = ({ label, value, accent = "slate", bold = false }) => {
  const colors = { amber: "text-amber-900", emerald: "text-emerald-900", slate: "text-slate-700" };
  return (
    <div className={`flex justify-between items-center py-1.5 ${colors[accent]}`}>
      <span className="text-xs">{label}</span>
      <span className={bold ? "font-extrabold text-base" : "font-bold text-sm"}>{value}</span>
    </div>
  );
};


const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default ProductionStage2;
